import {
	Component,
	OnInit,
	ViewChild,
	ChangeDetectionStrategy,
} from "@angular/core"
import * as moment from "moment"
import { ConfigDefinitions, daysOfWeek } from "@app/definitions/types"
import { GenAutocompleteComponent } from "@app/gen-inputs/gen-autocomplete/gen-autocomplete.component"
import { Modalable } from "@app/Modalable"
import { COMMON_IMPORTS } from "../common-imports"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { AutocompleteWithTagsComponent } from "@app/gen-inputs/autocomplete-with-tags/autocomplete-with-tags.component"
import { AutocompleteMultiComponent } from "@app/gen-inputs/autocomplete-multi/autocomplete-multi.component"
import { SpinOrContent } from "@app/utils/utils.component"
import { DateFormatPipe } from "@app/utils/date-format.pipe"
import { DatePickerComponent } from "@app/utils/date-picker/date-picker.component"
import { TimeFormatPipe } from "@app/utils/time-format.pipe"

@Component({
	selector: "app-meeting-times",
	imports: [
		COMMON_IMPORTS,
		GenAutocompleteComponent,
		GenButtonGroupComponent,
		GenInputComponent,
		LangValPipe,
		AutocompleteWithTagsComponent,
		AutocompleteMultiComponent,
		SpinOrContent,
		DateFormatPipe,
		DatePickerComponent,
		TimeFormatPipe,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./meeting-times.component.html",
})
export class MeetingTimesComponent extends Modalable {
	actualObj: any = null
	validationFields: any = {
		//field validations, control displaying field errors and ability to submit the form
		meetingtype_id: { is_legit_fuzzy_value: true },
		length: { not_empty: true, is_positive: true, is_integer: true },
		assoc_sub_user_id: { is_legit_fuzzy_value: true },
	}
	isFormSubmittable: boolean = false
	errors: any = {} //will hold errors for the fields in the form
	@ViewChild("meetingtypeAc") meetingtypeAc: GenAutocompleteComponent

	repeatOptions: string[] = [
		"one_time",
		"series_weekly",
		"series_monthly",
		"series_2weekly",
		"series_3weekly",
	]
	periodLen: number = 7
	meetingPadMinutes: number = 10

	dates: any = {}
	availableDates: string[] = []
	newWeeksDates: any = {}
	datesToDays: any = {}
	availableTimeChunksDates: any[] = []

	startDate: string = ""
	endDate: string = ""

	cc: any = null
	hasLocations: boolean = false
	hasAssocSelect: boolean = false
	excludeIds: number[] = []
	fusDataObj: any = null
	locations: any[] = []
	isCalculating: boolean = false

	async ngOnInit() {
		this.cc = this.store.cc()
		this.hasAssocSelect =
			this.permsService.owner_has_users &&
			this.permsService.perms?.administrative
		this.locations = this.store.getSiteDataTable("locations")
		this.actualObj = {
			meetingtype_id: 0,
			series_repeat: "one_time",
			length: 0,
			locationIds: [],
			assoc_sub_user_id: this.hasAssocSelect ? 0 : this.cc?.user_id,
		}
		this.excludeIds = this.store.getNonClinicalSubUserIds()
		this.hasLocations =
			this.permsService.owner_has_users &&
			(!this.cc?.only_therapist_id || this.cc?.locations_only)
		if (this.hasLocations) {
			this.actualObj.locationIds = this.locations.map((it) => it.id)
		}

		this.fusDataObj = await this.store.getAllFus()
		this.startDate = moment.utc().format(ConfigDefinitions.momentDateFormat)
		this.endDate = ""

		const meetingtype_id = this.store
			.getSiteDataTable("meetingtypes")
			.find((it) => it?.is_default == "yes")?.id
		if (meetingtype_id) {
			this.actualObj.meetingtype_id = meetingtype_id
			this.changeMeetingtype()
			await this.store.timeout(50)
			if (this.meetingtypeAc) {
				this.meetingtypeAc.updateSearchStr()
			}
		}
	}

	chooseAssoc(arr: any[]) {
		this.actualObj!.assoc_sub_user_id = arr?.length ? arr[0].id : 0
		this.periodGo()
	}

	areItemsTimeConflicting(it1: any, it2: any) {
		const d = "2023-04-11"
		const start1 = moment.utc(`${d} ${it1.time}`)
		const end1 = moment.utc(`${d} ${it1.time_to}`)
		const start2 = moment.utc(`${d} ${it2.time}`)
		const end2 = moment.utc(`${d} ${it2.time_to}`)

		if (start1.isBetween(start2, end2, null, "[)")) {
			return true
		}
		if (end1.isBetween(start2, end2, null, "(]")) {
			return true
		}
		if (start2.isBetween(start1, end1, null, "[)")) {
			return true
		}
		if (end2.isBetween(start1, end1, null, "(]")) {
			return true
		}
		return false
	}

	async findFusTimeBlocksForDate(
		currentDate: any,
		possiblyConflictingFuses: any[],
		location_id: number = 0
	) {
		let blocks: any[] = []
		const date = currentDate.format(ConfigDefinitions.momentDateFormat)

		for (let fus of possiblyConflictingFuses) {
			if (
				fus.series_limit_date &&
				moment.utc(fus.series_limit_date) < currentDate
			) {
				continue
			}
			if (
				fus.series_repeat == "one_time" &&
				moment.utc(fus.date_to) < currentDate
			) {
				continue
			}

			if (currentDate.day() != fus.start_day) {
				continue
			}

			if (this.permsService.owner_has_users) {
				const assocIds = this.store.getFusSpreadAssocIds(fus)
				if (
					assocIds?.length &&
					assocIds[0] != String(this.actualObj?.assoc_sub_user_id)
				) {
					if (this.hasLocations && fus?.location_id != location_id) {
						continue
					}
				}
			} else {
				if (this.hasLocations && fus?.location_id != location_id) {
					continue
				}
			}

			// if(this.hasAssocSelect){
			//   const assocIds=this.store.getFusSpreadAssocIds(fus);
			//   if(assocIds?.length && assocIds[0]!=String(this.actualObj.assoc_sub_user_id)){
			//     // continue;
			//   }
			// }

			//from each of these fuses, in this date, create a possible fus, see if there's overlap
			const posFus: any = {
				assoc_sub_user_ids: [this.actualObj?.assoc_sub_user_id],
				followuptype_id: 1,
				followupserie_date: date,
				followupserie_time: fus.time,
				length: this.actualObj?.length,
				series_repeat: this.actualObj?.series_repeat,
				series_limit_date: null,
				id: null,
				location_id,
				followupserie_date_to: date,
			}
			const overlapFus = await this.overlapService.find_overlap(posFus)
			if (overlapFus) {
				blocks.push({
					time: fus.time,
					time_to: this.getTimeToByTimeAndLength(
						fus.time,
						fus.length + this.meetingPadMinutes
					),
				})
			}
		}
		if (this.fusDataObj?.meeting_requests?.length) {
			for (let mr of this.fusDataObj.meeting_requests) {
				if (!mr.date) {
					continue
				}
				if (!mr.meetingtype_id) {
					continue
				}
				if (mr?.status != "new") {
					continue
				}
				const mrMom = moment.utc(mr.date)
				if (mrMom < currentDate) {
					continue
				}
				if (currentDate.day() != mrMom.day()) {
					continue
				}

				const meetingtype = this.store.searchSiteDataItem(mr.meetingtype_id, [
					"meetingtypes",
				])
				if (!meetingtype) {
					continue
				}
				const time_to = moment
					.utc(mr.date + " " + mr.time)
					.add(meetingtype.length + this.meetingPadMinutes, "minutes")
					.format("HH:mm")

				const posFus: any = {
					assoc_sub_user_ids: [this.actualObj?.assoc_sub_user_id],
					followuptype_id: 1,
					followupserie_date: date,
					followupserie_time: mr.time,
					length: this.actualObj?.length,
					series_repeat: this.actualObj?.series_repeat,
					series_limit_date: null,
					id: null,
					location_id,
					followupserie_date_to: date,
				}
				const overlapFus = await this.overlapService.find_overlap(posFus)
				if (overlapFus) {
					blocks.push({ time: mr.time, time_to })
				}
			}
		}
		if (
			this.permsService?.owner_has_users &&
			this.cc?.time_room_allocation == "yes"
		) {
			const tas = this.store
				.getSiteDataTable("time_allocations")
				.filter(
					(ta) =>
						ta.day == currentDate.day() &&
						ta.location_id &&
						ta.location_id == location_id &&
						this.store.searchSiteDataItem(ta.location_id, ["locations"])
							?.is_conflicting == "yes" &&
						ta.user_id != this.actualObj?.assoc_sub_user_id
				)
			tas.forEach((ta) => {
				blocks.push({ time: ta.time, time_to: ta.time_to })
			})
		}

		blocks.sort(
			(a, b) =>
				new Date(this.startDate + " " + a.time).getTime() -
				new Date(this.startDate + " " + b.time).getTime()
		)

		return blocks
	}
	getTimeToByTimeAndLength(time: string, length: number) {
		return moment
			.utc("2023-04-11 " + time)
			.add(length, "m")
			.format("HH:mm")
	}

	async getTimeChunksForLocation(
		currentDate: any,
		possiblyConflictingFuses: any[],
		location_id: number = 0
	) {
		const blockedTimes = await this.findFusTimeBlocksForDate(
			currentDate,
			possiblyConflictingFuses,
			location_id
		)

		if (
			this.permsService?.owner_has_users &&
			this.cc?.time_room_allocation == "yes"
		) {
			const tas = this.store
				.getSiteDataTable("time_allocations")
				.filter((ta) => ta.user_id == this.actualObj?.assoc_sub_user_id)
			if (tas?.length) {
				const thisDayTas = tas.filter((ta) => ta.day == currentDate.day())
				if (!thisDayTas?.length) {
					return []
				}
				const dayChunks: any[] = []
				blockedTimes.forEach((blockedTime: any) => {
					blockedTime.start = new Date(this.startDate + " " + blockedTime.time)
					blockedTime.end = new Date(this.startDate + " " + blockedTime.time_to)
				})

				thisDayTas.forEach((ta) => {
					const taStart = new Date(this.startDate + " " + ta.time)
					const taEnd = new Date(this.startDate + " " + ta.time_to)

					const participatingBlocks: any[] = []
					for (let blockedItem of blockedTimes) {
						if (blockedItem.end <= taStart) {
							//
						} else if (
							blockedItem.start <= taStart &&
							blockedItem.end >= taStart &&
							blockedItem.end <= taEnd
						) {
							ta.time = blockedItem.time_to
						} else if (
							blockedItem.start <= taStart &&
							blockedItem.end >= taEnd
						) {
							return
						} else if (
							blockedItem.start >= taStart &&
							blockedItem.end <= taEnd
						) {
							participatingBlocks.push(blockedItem)
						} else if (blockedItem.start < taEnd && blockedItem.end >= taEnd) {
							ta.time_to = blockedItem.time
						} else if (blockedItem.start >= taEnd) {
							//
						}
					}

					dayChunks.push(
						...this.getDayChunks(
							ta.time,
							ta.time_to,
							participatingBlocks,
							location_id
						)
					)
				})
				return dayChunks
			} else {
				return []
			}
		}

		return this.getDayChunks(
			this.cc.calendar_start_time,
			this.cc.calendar_end_time,
			blockedTimes,
			location_id
		)
	}
	getDayChunks(
		startTime: string,
		endTime: string,
		blockedTimes: any[],
		location_id: number
	) {
		const dayChunks: any[] = []
		const location = location_id
			? this.locations.find((it) => it.id == location_id)?.name
			: null
		const lenPlusPad = +(this.actualObj?.length || 0) + this.meetingPadMinutes
		//check start of day,after each end of times - do they conflict with the next meeting
		const timesToCheck = [{ time_to: startTime }, ...blockedTimes]
		timesToCheck.forEach((timeToCheck, key) => {
			const start = timeToCheck.time_to

			const obj = {
				time: start,
				time_to: this.getTimeToByTimeAndLength(start, lenPlusPad),
			}
			for (let t of blockedTimes) {
				if (this.areItemsTimeConflicting(obj, t)) {
					return
				}
			}
			const end = timesToCheck[key + 1]?.time || endTime
			const lengthMins =
				(new Date(this.startDate + " " + end).getTime() -
					new Date(this.startDate + " " + start).getTime()) /
				(1000 * 60)

			dayChunks.push({ start, end, location_id, location, lengthMins })
		})
		return dayChunks
	}

	async loadTimes() {
		this.fusDataObj = await this.store.getAllFus()

		// this.startDate=moment.utc().format(ConfigDefinitions.momentDateFormat);

		const start = moment.utc(this.startDate)
		this.startDate = start.format(ConfigDefinitions.momentDateFormat)
		this.endDate = moment
			.utc(start)
			.add(this.periodLen - 1, "days")
			.format(ConfigDefinitions.momentDateFormat)

		const fuses = this.fusDataObj.followupseries

		const from = moment.utc(this.startDate + " 00:00:00Z")
		const to = moment.utc(this.endDate + " 00:00:00Z").add(1, "day")
		if (from >= to) {
			return []
		}

		let followupseries = fuses.filter(
			(fus) => fus.series_repeat != "series_exception"
		)

		const possiblyConflictingFuses = followupseries.filter((fus) => {
			if (fus.series_limit_date && moment.utc(fus.series_limit_date) < from) {
				return false
			}
			if (fus.followuptype_id != 1) {
				return false
			}
			if (fus.takes_place == "no") {
				return false
			}
			if (fus.series_repeat == "series_exception") {
				return false
			}
			if (fus.status != "active") {
				return false
			}

			return true
		})

		let currentDate = (moment as any)(from)
		const daysChunks = {}

		while (currentDate < to) {
			const d = currentDate.format(ConfigDefinitions.momentDateFormat)
			let dayChunks: any[] = []

			if (this.hasLocations) {
				for (let locId of this.actualObj?.locationIds || []) {
					dayChunks = [
						...dayChunks,
						...(await this.getTimeChunksForLocation(
							currentDate,
							possiblyConflictingFuses,
							locId
						)),
					]
				}
			} else {
				dayChunks = await this.getTimeChunksForLocation(
					currentDate,
					possiblyConflictingFuses
				)
			}
			dayChunks = dayChunks.filter((it: any) => {
				return it.lengthMins >= Number(this.actualObj?.length)
			})

			if (dayChunks.length) {
				daysChunks[d] = dayChunks
			}

			currentDate.add(1, "day")
		}
		this.availableDates = Object.keys(daysChunks)
		this.newWeeksDates = {}

		let curDay = 0
		this.availableDates.forEach((d) => {
			const dd = new Date(d + " 12:00:00Z")
			const day = dd.getDay()
			if (curDay > day) {
				this.newWeeksDates[d] = 1
			}
			curDay = day
			this.datesToDays[d] = daysOfWeek[day]

			const dayChunks = daysChunks[d]
			dayChunks.sort(
				(a, b) =>
					new Date(this.startDate + " " + a.start).getTime() -
					new Date(this.startDate + " " + b.start).getTime()
			)

			this.availableTimeChunksDates[d] = dayChunks
		})

		this.isCalculating = false

		return
	}

	async openMeetingModal(date: string, block: any) {
		const creatingObj: any = {
			date,
			time: block.start,
			meetingtype_id: this.actualObj?.meetingtype_id,
			series_repeat: this.actualObj?.series_repeat,
			length: this.actualObj?.length,
		}
		if (this.hasLocations) {
			creatingObj.location_id = block.location_id
		}
		if (this.hasAssocSelect) {
			creatingObj.assoc_sub_user_id = this.actualObj?.assoc_sub_user_id
		}

		this.router.navigateByUrl("/calendar")
		this.onClose.emit()

		// this.chosenHourOut.emit({date,meetingtype_id:this.meetingtype.id,time});
		await this.store.timeout(200)
		this.store.calendarOpenMeeting2Subject.next(creatingObj)
	}
	chDate(date: string) {
		this.startDate = date
		this.periodGo()
	}
	async periodGo(num: number = 0) {
		let start = moment.utc(this.startDate)
		if (num != 0) {
			start.add(num * this.periodLen, "days")
		}
		if (start.isBefore(moment.utc())) {
			start = moment.utc()
		}
		this.startDate = start.format(ConfigDefinitions.momentDateFormat)
		this.endDate = moment
			.utc(start)
			.add(this.periodLen - 1, "days")
			.format(ConfigDefinitions.momentDateFormat)

		this.validateEntireForm()
		if (!this.isFormSubmittable) {
			return
		}
		this.isCalculating = true
		await this.store.timeout(50)
		this.loadTimes()
	}

	changeMeetingtype() {
		if (this.actualObj?.meetingtype_id) {
			const meetingtype = this.store.searchSiteDataItem(
				this.actualObj.meetingtype_id,
				["meetingtypes"]
			)
			this.actualObj.length = meetingtype.length
		}
		this.periodGo()
	}

	validateEntireForm() {
		this.isFormSubmittable = this.validator.validateEntireForm(
			this.actualObj,
			this.errors,
			this.validationFields
		)
	}
	chooseTagsForLocations(tagObjs: any[]) {
		this.actualObj.locationIds = []

		if (tagObjs?.length) {
			const tagIds = tagObjs.map((it) => Number(it.id))
			//get user ids to tags
			const locIdsToTagArray = {}
			const o2t = this.store
				.getSiteDataTable("object_to_tags")
				.filter((row) => row.item_type == "location")
			o2t.forEach((o2tRow) => {
				if (!locIdsToTagArray[o2tRow.item_id]) {
					locIdsToTagArray[o2tRow.item_id] = []
				}
				locIdsToTagArray[o2tRow.item_id].push(Number(o2tRow.tag_id))
			})
			//only get total cross of arrays
			Object.keys(locIdsToTagArray).forEach((loc_id) => {
				const locTagArray = locIdsToTagArray[loc_id]
				if (tagIds.every((tag_id) => locTagArray.includes(tag_id))) {
					this.actualObj.locationIds.push(Number(loc_id))
				}
			})
		} else {
			this.actualObj.locationIds = this.locations.map((it) => it.id)
		}
		this.periodGo()
	}
}
